import { useQuery } from "@apollo/client/react";
import { Typography } from "antd";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router";
import { z } from "zod";
import { PageLoader } from "../../components/PageLoader.tsx";
import { RefreshProvider } from "../../context/RefreshContext.tsx";
import { GetIngredientByVersionDocument, VersionStatus } from "../../gql/generated/graphqlSchema.tsx";
import { useSafeParams } from "../../hooks/useSafeParams.ts";
import { routes } from "../../routes.ts";
import { IngredientProvider } from "./IngredientContext.tsx";
import { IngredientDetailView } from "./view/IngredientDetailView.tsx";

const paramsSchema = z.object({
  id: z.string().min(1),
  version: z.coerce.number().int().positive(),
});

const noop = () => {};

const IngredientDetailVersionPageInner = () => {
  const { t } = useTranslation();
  const { id, version } = useSafeParams(paramsSchema);
  const { data, loading, refetch } = useQuery(GetIngredientByVersionDocument, {
    variables: { id, versionNumber: version },
  });

  if (loading) {
    return <PageLoader />;
  }

  const ingredient = data?.ingredientByVersion;
  if (!ingredient) {
    return <Typography.Text type="secondary">{t("ingredients.notFound")}</Typography.Text>;
  }

  if (ingredient.versionStatus !== VersionStatus.Archived) {
    return <Navigate replace to={routes.ingredientDetail.to(id)} />;
  }

  return (
    <IngredientProvider ingredient={ingredient} readOnly refetch={() => refetch()} setIngredient={noop}>
      <IngredientDetailView key={ingredient.versionId} />
    </IngredientProvider>
  );
};

// eslint-disable-next-line react/no-multi-comp
export const IngredientDetailVersionPage = () => (
  <RefreshProvider>
    <IngredientDetailVersionPageInner />
  </RefreshProvider>
);
